import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  OutlineButton,
  Input,
  Select,
  TextArea,
  FormGroup,
  Label,
  ErrorText,
  Grid,
  FlexContainer
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { User, UserRole, UserStatus } from '../../types';
import apiService from '../../services/api';
import CustomDialog from '../common/CustomDialog';

const ActivationLinkModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(Card)`
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  h3 {
    margin: 0;
    color: var(--secondary-color);
  }
  
  button {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: var(--dark-gray);
    
    &:hover {
      color: var(--danger-color);
    }
  }
`;

const ModalBody = styled.div`
  margin-bottom: 1.5rem;
  
  p {
    margin: 0 0 1rem 0;
    color: var(--text-color);
    line-height: 1.5;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LinkBox = styled.div`
  background: var(--light-gray);
  border: 2px dashed var(--primary-color);
  border-radius: var(--border-radius);
  padding: 1rem;
  margin: 1rem 0;
  word-break: break-all;
  font-family: monospace;
  font-size: 0.9rem;
  color: var(--primary-color);
`;

const CopyButton = styled(Button)`
  margin-top: 0.5rem;
`;

const BackButton = styled(OutlineButton)`
  margin-bottom: 1rem;
`;

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const InfoBox = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: var(--light-gray);
  border-radius: var(--border-radius);
  
  h5 {
    margin: 0 0 0.5rem 0;
    color: var(--secondary-color);
  }
  
  ul {
    margin: 0;
    padding-left: 1.5rem;
  }
`;

interface FormErrors {
  name?: string;
  email?: string;
  role?: string;
  status?: string;
  phone?: string;
  address?: string;
  department?: string;
}

const UserForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<{
    name: string;
    email: string;
    role: UserRole;
    status: UserStatus;
    phone: string;
    address: string;
    department: string;
  }>({
    name: '',
    email: '',
    role: 'Supervisor',
    status: 'Inactive',
    phone: '',
    address: '',
    department: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(isEditMode);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [activationLink, setActivationLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [showActivationLink, setShowActivationLink] = useState(false);
  
  // Dialog state
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  // Check if current user has admin privileges
  const canManageUsers = currentUser?.role === 'MD' || currentUser?.role === 'TD';

  const showDialog = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    if (!canManageUsers) {
      // Redirect if user doesn't have permission
      navigate('/dashboard');
      return;
    }

    const loadUserData = async () => {
      if (isEditMode && id) {
        try {
          setLoading(true);
          const userResponse = await apiService.getUser(id);
          const userData = userResponse.data;

          setFormData({
            name: userData.name,
            email: userData.email,
            role: userData.role,
            status: userData.status,
            phone: userData.contactDetails?.phone || '',
            address: userData.contactDetails?.address || '',
            department: userData.contactDetails?.department || '',
          });
        } catch (error) {
          console.error('Error loading user data:', error);
          navigate('/users');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    loadUserData();
  }, [id, isEditMode, canManageUsers, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when field is edited
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (!formData.status) {
      newErrors.status = 'Status is required';
    }



    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitLoading(true);

    try {
      // Prepare user data in the format expected by backend
      const userData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        status: formData.status,
        contactDetails: {
          phone: formData.phone,
          address: formData.address,
          department: formData.department
        }
      };

      if (isEditMode && id) {
        // Update existing user
        await apiService.updateUser(id, userData);
        showDialog('Success', 'User updated successfully', 'success');
        navigate('/users');
      } else {
        // Create new user
        const response = await apiService.createUser(userData);

        if (response.success) {
          // Use the real activation link from backend
          const activationLink = response.data.activationLink;

          setActivationLink(activationLink);
          setShowActivationLink(true);
        } else {
          showDialog('Error', 'Error creating user. Please try again.', 'error');
        }
      }
    } catch (error: any) {
      console.error('Error saving user:', error);
      const errorMessage = error.message || 'Error saving user. Please try again.';
      showDialog('Error', errorMessage, 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(activationLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback for older browsers or non-HTTPS
        const textArea = document.createElement('textarea');
        textArea.value = activationLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error('Fallback copy failed:', err);
          // Show manual copy instructions
          showDialog('Copy Instructions', 
            'Please manually copy this link: ' + activationLink, 
            'info');
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Show manual copy instructions
      showDialog('Copy Instructions', 
        'Please manually copy this link: ' + activationLink, 
        'info');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <BackButton as={Link} to="/users">
        &larr; Back to Users List
      </BackButton>

      <h1 className="page-title">{isEditMode ? 'Edit User' : 'Add New User'}</h1>

      <FormContainer>
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <h3>User Information</h3>
            </CardHeader>
            <CardBody>
              <Grid columns="1fr 1fr" gap="1rem">
                <FormGroup>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? 'error' : ''}
                  />
                  {errors.name && <ErrorText>{errors.name}</ErrorText>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && <ErrorText>{errors.email}</ErrorText>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className={errors.role ? 'error' : ''}
                  >
                    <option value="MD">MD (Managing Director)</option>
                    <option value="TD">TD (Technical Director)</option>
                    <option value="Project Manager">Project Manager</option>
                    <option value="Accountant">Accountant</option>
                    <option value="Cashier">Cashier</option>

                    <option value="Store-Keeper (Logistics)">Store-Keeper (Logistics)</option>
                    <option value="Procurement">Procurement</option>
                    <option value="Supervisor">Supervisor</option>
                    <option value="HR (Admin)">HR (Admin)</option>
                    <option value="Fleet Manager">Fleet Manager</option>
                  </Select>
                  {errors.role && <ErrorText>{errors.role}</ErrorText>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="status">Status *</Label>
                  <Select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className={errors.status ? 'error' : ''}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                  </Select>
                  {errors.status && <ErrorText>{errors.status}</ErrorText>}
                </FormGroup>
              </Grid>
            </CardBody>
          </Card>

          <Card style={{ marginTop: '1.5rem' }}>
            <CardHeader>
              <h3>Contact Details</h3>
            </CardHeader>
            <CardBody>
              <Grid columns="1fr 1fr" gap="1rem">
                <FormGroup>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={errors.phone ? 'error' : ''}
                  />
                  {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className={errors.department ? 'error' : ''}
                  />
                  {errors.department && <ErrorText>{errors.department}</ErrorText>}
                </FormGroup>

                <FormGroup style={{ gridColumn: '1 / span 2' }}>
                  <Label htmlFor="address">Address</Label>
                  <TextArea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={errors.address ? 'error' : ''}
                  />
                  {errors.address && <ErrorText>{errors.address}</ErrorText>}
                </FormGroup>
              </Grid>
            </CardBody>
          </Card>

          <Card style={{ marginTop: '1.5rem' }}>
            <CardHeader>
              <h3>Account Activation</h3>
              <small>After creating the user, an activation link will be generated for password setup</small>
            </CardHeader>
            <CardBody>
              <div style={{
                background: 'var(--light-gray)',
                padding: '1rem',
                borderRadius: 'var(--border-radius)',
                marginBottom: '1rem'
              }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--secondary-color)' }}>
                  User Activation Workflow
                </h4>
                <ol style={{ margin: '0', paddingLeft: '1.5rem', color: 'var(--dark-gray)' }}>
                  <li>User account is created with "Inactive" status</li>
                  <li>Activation link is generated and sent via email</li>
                  <li>User clicks link and sets their password</li>
                  <li>Account is automatically activated upon password setup</li>
                  <li>User can then sign in with their email and password</li>
                </ol>
              </div>
            </CardBody>

            <CardFooter>
              <OutlineButton as={Link} to="/users" type="button">
                Cancel
              </OutlineButton>
              <Button type="submit" disabled={submitLoading}>
                {submitLoading ? 'Creating User...' : isEditMode ? 'Update User' : 'Create User'}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </FormContainer>

      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>

      {/* Activation Link Modal */}
      {showActivationLink && (
        <ActivationLinkModal>
          <ModalContent>
            <ModalHeader>
              <h3>User Created Successfully!</h3>
              <button onClick={() => setShowActivationLink(false)}>&times;</button>
            </ModalHeader>
            
            <ModalBody>
              <p>
                The user has been created successfully. Please provide them with the activation link below to set their password.
              </p>
              
              <div>
                <Label>Activation Link:</Label>
                <LinkBox>
                  {activationLink}
                </LinkBox>
                
                <CopyButton onClick={copyToClipboard}>
                  {copied ? 'Copied!' : 'Copy Link'}
                </CopyButton>
                
                <InfoBox>
                  <h5>Next Steps:</h5>
                  <ul>
                    <li>Copy the activation link above</li>
                    <li>Send it to the user via email or message</li>
                    <li>The user will use this link to set their password</li>
                    <li>The link expires in 24 hours</li>
                  </ul>
                </InfoBox>
              </div>
            </ModalBody>
            
            <ModalFooter>
              <OutlineButton onClick={() => setShowActivationLink(false)}>
                Close
              </OutlineButton>
              <Button onClick={() => navigate('/users')}>
                Go to Users List
              </Button>
            </ModalFooter>
          </ModalContent>
        </ActivationLinkModal>
      )}

      {/* Custom Dialog */}
      <CustomDialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        confirmText="OK"
        showCancel={false}
      />
    </div>
  );
};

export default UserForm; 